import axios from 'axios';
import { buildQueryString } from '../utils/searchFilter';

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;
const USER_API = axios.create({ baseURL: `${BACKEND_BASE_URL}/api/v1/user` });


// Request interceptor to add authorization and accept headers
USER_API.interceptors.request.use(
  (req) => {
    const userToken = localStorage.getItem('token');
    if (userToken) {
        req.headers.Authorization = `Bearer ${userToken}`;
        req.headers.Accept = 'application/json';
    }
    return req;
  },
  (error) => {
    // console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle responses and errors
USER_API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with a status other than 2xx
      if (error.response.status === 401) {
        // console.error('Unauthorized access - possibly due to expired token');
      } else {
        const responseErrors = error?.response?.data?.errors;
        const fallbackMessage = error?.response?.data?.message || "An error occurred";
        if (responseErrors) {
          // console.error('Response errors:', responseErrors);
        } else {
          // console.error('Response error:', fallbackMessage);
        }
      }
    } else if (error.request) {
      // Request was made but no response received
      // console.error('No response received:', error.request);
    } else {
      // Something else caused the error
      // console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export const allowedTransferMethods = (countryCode) => USER_API.get(`/send-money/transfer-options?country=${countryCode}`);
export const getSupportedCountryStates = () => USER_API.get('countries-with-states');

export const getUserProfile = () => USER_API.get('/profile');
export const getUserDashboardInfo = () => USER_API.get('/dashboard');

export const updateUserProfile = (credentials, userId) => USER_API.put(`/update-profile/${userId}`, credentials);
export const updateUserPassword = (credentials, userId) => USER_API.put(`/update-password/${userId}`, credentials);

export const initiateKyc = (credentials) => USER_API.post('/kyc/start', credentials);
export const verifyGovernmentId = (credentials) => USER_API.post('/kyc/government-issued-id/verify', credentials);
export const setupVirtualAccount = (credentials) => USER_API.post('/kyc/virtual-account/setup', credentials);
export const updateKycDetails = (credential) => USER_API.post('/update-kyc-details', credential);
export const updateTier2KycInfo = (payload) => USER_API.post('/kyc/tier-2/update-info', payload);
export const uploadTier2KycDocument = (formData) => USER_API.post('/kyc/tier-2/upload-document', formData);
export const submitTier2Kyc = () => USER_API.post('/kyc/tier-2/submit');

// KYB (Business KYC) endpoints
export const getKybSummary = () => USER_API.get('/kyb/summary');
export const getKybOnboardingOptions = (countryCode) => USER_API.get(`/kyb/onboarding-options?country_code=${countryCode}`);
export const updateKybBasicInfo = (formData) => USER_API.post('/kyb/update-basic-info', formData);
export const updateKybAddress = (credentials) => USER_API.post('/kyb/update-address', credentials);
export const uploadKybDocument = (formData) => USER_API.post('/kyb/upload-document', formData);
export const deleteKybDocument = (documentId) => USER_API.post(`/kyb/documents/${documentId}/delete`);
export const storeKybDirector = (credentials) => USER_API.post('/kyb/directors/store', credentials);
export const updateKybDirector = (directorId, credentials) => USER_API.post(`/kyb/directors/${directorId}/update`, credentials);
export const updateKybDirectorAddress = (directorId, credentials) => USER_API.post(`/kyb/directors/${directorId}/update-address`, credentials);
export const markDirectorAsPrimaryOwner = (directorId) => USER_API.post(`/kyb/directors/${directorId}/mark-as-primary-owner`);
export const sendDirectorKycRequest = (directorId) => USER_API.post(`/kyb/directors/${directorId}/start-kyc`);
export const deleteKybDirector = (directorId) => USER_API.post(`/kyb/directors/${directorId}/delete`);
export const completeDirectorKyc = (kycRef, payload = {}) => USER_API.post(`/kyb/directors/${kycRef}/complete-kyc`, payload);
export const updateDirectorKycStatus = (kycRef, formData) => USER_API.post(`/kyb/directors/${kycRef}/update-kyc-status`, formData);
export const submitKybRequest = () => USER_API.post(`/kyb/submit`);
export const updateBusinessDirectorInvite = (invitationReference, credentials) => USER_API.post(`kyb/directors/invites/${invitationReference}/update-status`, credentials);

export const userWorkExperience = (credentials) => USER_API.post('/work-experiences', credentials);
export const getUserWorkExperience = (filters = {}) => USER_API.get(`/work-experiences?${buildQueryString(filters)}`);

export const userDocsUpload = (credentials) => USER_API.post('/documents/upload', credentials);
export const getUserDocsUpload = (filters = {}) => USER_API.get(`/documents?${buildQueryString(filters)}`);

export const userIncomeSources = (credentials) => USER_API.post('/income-sources', credentials);
export const getUserIncomeSources = (filters = {}) => USER_API.get(`/income-sources?${buildQueryString(filters)}`);

export const phoneVerificationRequest = (credentials) => USER_API.post('/phone-verification/send-otp', credentials);
export const phoneNumberVerification = (credentials) => USER_API.post('/phone-verification/verify-otp', credentials);

export const getAllWallets = () => USER_API.get('/wallet/balance');
export const addWallet = (credential) => USER_API.post('/wallet', credential);
export const deleteWallet = (walletId) => USER_API.delete(`/wallet/${walletId}`);


export const setTransactionPin = (credentials) => USER_API.post('/pin/set', credentials);
export const changeTransactionPin = (credentials) => USER_API.post('/pin/change', credentials);
export const setNewTransactionPin = (credentials) => USER_API.post('/pin/reset/reset', credentials);
export const resetTransactionPinRequest = () => USER_API.post('/pin/reset/request');
export const resetTransactionPin = (credentials) => USER_API.post('/pin/reset', credentials);

// export const referralLeaderboard = (filters = {}) => USER_API.get(`/leaderboard/referrals?${buildQueryString(filters)}`);
export const referralLeaderboard = (perPage, page) => USER_API.get(`/leaderboard/referrals?per_page=${perPage}&page=${page}`);
export const retrieveReferrals = (perPage, page) => USER_API.get(`/referrals-and-earnings?per_page=${perPage}&page=${page}`);

export const createSupportTicket = (credentials) => USER_API.post('/support-ticket', credentials);
export const getSupportTicketList = (filters = {}) => USER_API.get(`/support-ticket/search?${buildQueryString(filters)}`);
export const getSupportTicketDetails = (ticketId) => USER_API.get(`/support-ticket/${ticketId}`);
export const supportReply = (ticketId, message, attachment) =>
  USER_API.post(`/support-ticket/${ticketId}/reply`, { message, attachment });

export default USER_API;